
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { PaymentPlatformProvider } from "./contexts/PaymentPlatformContext";
import ProtectedRoute from "./components/ProtectedRoute";
import AppLayout from "./components/navigation/AppLayout";
import { DeepLinkHandler } from "./components/DeepLinkHandler";
import { ProviderOnboardingBanner } from "./components/ProviderOnboardingBanner";
import { AppBadgeSync } from "./components/AppBadgeSync";

// Import pages
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Feed from "./pages/Feed";
import Profile from "./pages/Profile";
import CreatePost from "./pages/CreatePost";
import MyPets from "./pages/MyPets";
import PetProfile from "./pages/PetProfile";
import EditPetPage from "./pages/EditPetPage";
import PostDetail from "./pages/PostDetail";
import Settings from "./pages/Settings";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Feedback from "./pages/Feedback";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import CookiePolicy from "./pages/CookiePolicy";
import ChildSafetyPolicy from "./pages/ChildSafetyPolicy";
import Subscription from "./pages/Subscription";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import PaymentResponse from "./pages/PaymentResponse";
import Notifications from "./pages/Notifications";
import NotFound from "./pages/NotFound";
import PetsByCategory from "./pages/PetsByCategory";
import Explore from "./pages/Explore";
import Community from "./pages/Community";
import PetCategories from "./pages/PetCategories";
import UpdatePassword from "./pages/UpdatePassword";
import ModerationPanel from "./pages/ModerationPanel";
import AdminManagementPage from "./pages/AdminManagementPage";
import SecureAdminManagementPage from "./pages/SecureAdminManagementPage";
import UserManagementPage from "./pages/UserManagementPage";
import AuthCallback from './pages/AuthCallback';
import AccountSuspended from "./pages/AccountSuspended";

// Medical Records pages
import MedicalHome from "./pages/MedicalRecords/MedicalHome";
import MedicalRecordList from "./pages/MedicalRecords/MedicalRecordList";
import VaccinationList from "./pages/MedicalRecords/VaccinationList";
import AppointmentList from "./pages/MedicalRecords/AppointmentList";
import AddEditMedicalRecordForm from "./pages/MedicalRecords/RecordForm/AddEditMedicalRecordForm";
import AddEditVaccinationForm from "./pages/MedicalRecords/AddEditVaccinationForm";
import AddEditAppointmentForm from "./pages/MedicalRecords/AddEditAppointmentForm";
import MedicalRecordDetail from "./pages/MedicalRecords/MedicalRecordDetail";
import FeedbackManagement from "./pages/FeedbackManagement";
import AuditMonitorPage from "./pages/AuditMonitorPage";
import MatchInbox from "./pages/MatchInbox";
import { MatchAnalytics } from "./pages/MatchAnalytics";
import PetMatchingInfo from "./pages/PetMatchingInfo";
import FindMatches from "./pages/FindMatches";
import PurchaseCredits from "./pages/PurchaseCredits";
import CreditsPurchaseSuccess from "./pages/CreditsPurchaseSuccess";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import PushNotificationTestPage from "./pages/PushNotificationTest";
import Services from "./pages/Services";
import ServicesSearch from "./pages/ServicesSearch";
import ProviderProfile from "./pages/ProviderProfile";
import RegisterProvider from "./pages/RegisterProvider";
import RegisterProviderSuccess from "./pages/RegisterProviderSuccess";
import ProviderDashboard from "./pages/ProviderDashboard";
import ServiceProvidersPage from "./pages/ServiceProvidersPage";
import PetFriendlyPlaces from "./pages/PetFriendlyPlaces";
import Donate from "./pages/Donate";
import DonationSuccess from "./pages/DonationSuccess";
import NutritionAdminPage from "./pages/NutritionAdminPage";
import AmbassadorLogin from "./pages/AmbassadorLogin";
import AmbassadorOnboarding from "./pages/AmbassadorOnboarding";
import AmbassadorDashboard from "./pages/AmbassadorDashboard";
import AmbassadorTerms from "./pages/AmbassadorTerms";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
      <Toaster />
      <Sonner />
      <AuthProvider>
        <NotificationProvider>
          <PaymentPlatformProvider>
            <DeepLinkHandler />
            <AppBadgeSync />
            <ProviderOnboardingBanner />
            <Routes>
            {/* Public routes */}
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/auth/account-suspended" element={<AccountSuspended />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/feedback" element={<Feedback />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/child-safety-policy" element={<ChildSafetyPolicy />} />
            <Route path="/pet-matching-info" element={<PetMatchingInfo />} />
            <Route path="/service-providers" element={<ServiceProvidersPage />} />
            <Route path="/pet/:petId" element={<PetProfile />} />
            <Route path="/post/:postId" element={<PostDetail />} />
            <Route path="/pets/category/:category" element={<PetsByCategory />} />
            <Route path="/update-password" element={<UpdatePassword />} />

            {/* Ambassador routes (web-only) */}
            <Route path="/ambassador-login" element={<AmbassadorLogin />} />
            <Route path="/ambassador-onboard" element={<AmbassadorOnboarding />} />
            <Route path="/ambassador-dashboard" element={<AmbassadorDashboard />} />
            <Route path="/ambassador-terms" element={<AmbassadorTerms />} />

            {/* Payment routes */}
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/payment/response" element={<PaymentResponse />} />
            <Route path="/purchase-credits" element={<PurchaseCredits />} />
            <Route path="/credits-purchase-success" element={<CreditsPurchaseSuccess />} />
            <Route path="/subscription-success" element={<SubscriptionSuccess />} />
            <Route path="/donate" element={<Donate />} />
            <Route path="/donation-success" element={<DonationSuccess />} />

            {/* Protected routes */}
            <Route element={<ProtectedRoute><AppLayout><Outlet /></AppLayout></ProtectedRoute>}>
              <Route path="/feed" element={<Feed />} />
              <Route path="/explore" element={<Explore />} />
              <Route path="/community" element={<Community />} />
              <Route path="/pet-categories" element={<PetCategories />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/create-post" element={<CreatePost />} />
              <Route path="/my-pets" element={<MyPets />} />
              <Route path="/edit-pet/:petId" element={<EditPetPage />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/subscription" element={<Subscription />} />
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/matches" element={<MatchInbox />} />
              <Route path="/matches/discover" element={<FindMatches />} />
              <Route path="/matches/analytics" element={<MatchAnalytics />} />
              <Route path="/moderation" element={<ModerationPanel />} />
              <Route path="/admin" element={<AdminManagementPage />} />
              <Route path="/feedback-management" element={<FeedbackManagement />} />
              <Route path="/admin/secure" element={<SecureAdminManagementPage />} />
              <Route path="/admin/user-management" element={<UserManagementPage />} />
              <Route path="/admin/test-push-notifications" element={<PushNotificationTestPage />} />
              <Route path="/audit" element={<AuditMonitorPage />} />
              <Route path="/admin/nutrition" element={<NutritionAdminPage />} />

              {/* Services routes */}
              <Route path="/services" element={<Services />} />
              <Route path="/services/search" element={<ServicesSearch />} />
              <Route path="/services/provider/:id" element={<ProviderProfile />} />
              <Route path="/register-provider" element={<RegisterProvider />} />
              <Route path="/register-provider/success" element={<RegisterProviderSuccess />} />
              <Route path="/provider-dashboard" element={<ProviderDashboard />} />

              {/* Pet Friendly Places */}
              <Route path="/places" element={<PetFriendlyPlaces />} />

              {/* Medical Records routes */}
              <Route path="/medical" element={<MedicalHome />} />
              <Route path="/medical/:petId" element={<MedicalHome />} />
              <Route path="/medical/:petId/records" element={<MedicalRecordList />} />
              <Route path="/medical/:petId/records/new" element={<AddEditMedicalRecordForm />} />
              <Route path="/medical/:petId/records/:recordId" element={<MedicalRecordDetail />} />
              <Route path="/medical/:petId/records/:recordId/edit" element={<AddEditMedicalRecordForm />} />
              <Route path="/medical/:petId/vaccinations" element={<VaccinationList />} />
              <Route path="/medical/:petId/vaccinations/new" element={<AddEditVaccinationForm />} />
              <Route path="/medical/:petId/vaccinations/edit/:vaccinationId" element={<AddEditVaccinationForm />} />
              <Route path="/medical/:petId/appointments" element={<AppointmentList />} />
              <Route path="/medical/:petId/appointments/new" element={<AddEditAppointmentForm />} />
              <Route path="/medical/:petId/appointments/edit/:appointmentId" element={<AddEditAppointmentForm />} />
            </Route>

            {/* Catch all route */}
            <Route path="*" element={<NotFound />} />
            </Routes>
          </PaymentPlatformProvider>
        </NotificationProvider>
      </AuthProvider>
  </QueryClientProvider>
);

export default App;
